import React, { useEffect, useState } from "react";
import InvitationCode from "./InvitationCode";
import { invitationsAPI } from "../api";
import { useInvitation } from "../contexts/InvitationContext";

interface RSVPPageProps {
  invitation: any;
}

interface RSVPData {
  rsvp_ceremony: boolean;
  rsvp_luncheon: boolean;
  rsvp_reception: boolean;
  family_attending: number;
  bringing_plus_one: boolean;
  plus_one_name?: string;
  rsvp: boolean;
}

interface EventDetails {
  key: "ceremony" | "luncheon" | "reception";
  title: string;
  time: string;
  location: string;
}

const RSVPPage: React.FC<RSVPPageProps> = ({ invitation }) => {
  const { refetchInvitation } = useInvitation();
  const [rsvpData, setRsvpData] = useState<RSVPData>({
    rsvp_ceremony: false,
    rsvp_luncheon: false,
    rsvp_reception: false,
    family_attending: 0,
    bringing_plus_one: false,
    plus_one_name: "",
    rsvp: true,
  });
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  useEffect(() => {
    if (!invitation) return;

    setRsvpData((prev) => ({
      ...prev,
      rsvp_ceremony: invitation.rsvp_ceremony,
      rsvp_luncheon: invitation.rsvp_luncheon,
      rsvp_reception: invitation.rsvp_reception,
      family_attending: invitation.family_attending,
      bringing_plus_one: invitation.bringing_plus_one || false,
      plus_one_name: invitation.plus_one_name || "",
    }));
  }, [invitation]);

  const autoSave = async (updatedData: RSVPData) => {
    if (!invitation) return;

    setIsAutoSaving(true);
    try {
      await invitationsAPI.updateInvitation(invitation.id, updatedData);
      await refetchInvitation();
    } catch (error) {
      console.error("Failed to auto-save RSVP:", error);
    } finally {
      setIsAutoSaving(false);
    }
  };

  const handleSectionToggle = async (
    section: "rsvp_ceremony" | "rsvp_luncheon" | "rsvp_reception"
  ) => {
    const updatedData = {
      ...rsvpData,
      [section]: !rsvpData[section],
    };
    setRsvpData(updatedData);
    await autoSave(updatedData);
  };

  const handleFamilyAttendingChange = async (value: string) => {
    const numValue = value === "" ? 0 : parseInt(value) || 0;
    const updatedData = {
      ...rsvpData,
      family_attending: Math.max(0, numValue),
    };
    setRsvpData(updatedData);
    await autoSave(updatedData);
  };

  const handlePlusOneNameChange = async (value: string) => {
    const updatedData = {
      ...rsvpData,
      plus_one_name: value,
    };
    setRsvpData(updatedData);
    await autoSave(updatedData);
  };

  const handleBringingPlusOneChange = async (checked: boolean) => {
    const updatedData = {
      ...rsvpData,
      bringing_plus_one: checked,
      plus_one_name: checked ? rsvpData.plus_one_name : "",
    };
    setRsvpData(updatedData);
    await autoSave(updatedData);
  };

  const eventDetails: EventDetails[] = [
    {
      key: "ceremony",
      title: "Ceremony",
      time: "5:00 PM",
      location: "Springville Art Museum",
    },
    {
      key: "luncheon",
      title: "Luncheon",
      time: "6:15 PM",
      location: "Springville Art Museum",
    },
    {
      key: "reception",
      title: "Reception",
      time: "7:30 PM",
      location: "Springville Art Museum",
    },
  ];

  const EventSection: React.FC<{
    event: EventDetails;
    isSelected: boolean;
    onClick: () => void;
  }> = ({ event, isSelected, onClick }) => (
    <div
      className={`event-section ${isSelected ? "selected" : ""}`}
      onClick={onClick}
    >
      <h3>{event.title}</h3>
      <p>{event.time}</p>
      <p>{event.location}</p>
      <div className="selection-indicator">
        {isSelected ? "✓ Attending" : "Not Attending"}
      </div>
    </div>
  );

  return (
    <div className="rsvp-page">
      <div className="wedding-announcement">
        <div className="announcement-header">
          <h2 className="couple-names">Michael & Sequoia</h2>
          <p className="wedding-date">September 18th, 2025</p>
        </div>

        {!invitation ? (
          <InvitationCode />
        ) : (
          <div className="announcement-body">
            <p className="invitation-text">
              We can't wait to celebrate our wedding with you!
            </p>
            <p className="invitation-text">
              Please select the events you are attending
            </p>

            <div className="event-details">
              {eventDetails.map(
                (event) =>
                  invitation[event.key] && (
                    <EventSection
                      key={event.key}
                      event={event}
                      isSelected={rsvpData[`rsvp_${event.key}`]}
                      onClick={() => handleSectionToggle(`rsvp_${event.key}`)}
                    />
                  )
              )}
            </div>

            <div className="dress-code-section">
              <h3>Dress Code</h3>
              <p className="dress-code-text">
                The dress code is semi-formal. Please feel free to wear whatever
                color you prefer. (Even white!)
              </p>
            </div>

            {invitation.family_allowed && (
              <div className="family-section">
                <h3>Family Attending</h3>
                <div className="family-input">
                  <label htmlFor="family-attending">
                    Number of family members:
                  </label>
                  <input
                    id="family-attending"
                    type="number"
                    min="0"
                    value={
                      rsvpData.family_attending === 0
                        ? ""
                        : rsvpData.family_attending
                    }
                    onChange={(e) =>
                      handleFamilyAttendingChange(e.target.value)
                    }
                    className="family-input-field"
                  />
                </div>
              </div>
            )}

            {invitation.plus_one && (
              <div className="plus-one-section">
                <h3>Plus One</h3>
                <div className="plus-one-checkbox">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={rsvpData.bringing_plus_one}
                      onChange={(e) =>
                        handleBringingPlusOneChange(e.target.checked)
                      }
                      className="plus-one-checkbox-input"
                    />
                    <span className="checkmark"></span>I will be bringing a plus
                    one
                  </label>
                </div>

                {rsvpData.bringing_plus_one && (
                  <div className="plus-one-name-section">
                    <p className="plus-one-instruction">
                      Please provide your plus one's name:
                    </p>
                    <div className="plus-one-input">
                      <label htmlFor="plus-one-name">Plus One Name:</label>
                      <input
                        id="plus-one-name"
                        type="text"
                        value={rsvpData.plus_one_name || ""}
                        onChange={(e) =>
                          handlePlusOneNameChange(e.target.value)
                        }
                        placeholder="Enter plus one name"
                        className="plus-one-input-field"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="rsvp-section">
              <h3>RSVP</h3>
              <p>Please respond by August 25th, 2025</p>

              {isAutoSaving && <div className="success-message">Saving...</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RSVPPage;
